import {
  KAI_SHIKI_SETTINGS_WIDGET_COLORS,
  KAI_SHIKI_TYPOGRAPHY,
  styleguideColors,
  styleguideFontSize,
  styleguideFontWeight,
} from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { css, SerializedStyles } from '@emotion/react';
import { styleguideControlSizes, styleguideFontFamily, withOpacity } from '@kaiizen/kai-shiki/core';

export type MainNavigationButtonStyles = {
  debug: SerializedStyles;
  primary: SerializedStyles;
  secondary: SerializedStyles;
};

export type MainNavigationStyles = {
  root: SerializedStyles;
  address: SerializedStyles;
  filler: SerializedStyles;
  actionsContainer: SerializedStyles;
};

export const MAIN_NAVIGATION_STYLES: MainNavigationStyles = {
  root: css`
    label: MAIN_NAVIGATION_ROOT_STYLE;
    height: ${styleguideControlSizes.topBarHeight}px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    padding: 0 ${styleguideControlSizes.topBarPaddingX}px;
    background: ${styleguideColors.ink};
    color: ${styleguideColors.paper};
  `,
  // 600 in the mockups renders as `bold` — see styleguideFontWeight's own
  // comment: CircularXX has no 600 face, so a semibold token would quietly
  // misrepresent itself as 700 anyway.
  address: css`
    label: MAIN_NAVIGATION_ADDRESS_STYLE;
    font-family: ${styleguideFontFamily.display};
    font-size: ${pxToRem(styleguideFontSize.displayAddress)};
    font-weight: ${styleguideFontWeight.bold};
    color: ${styleguideColors.white};
    letter-spacing: -0.01em;
    cursor: pointer;
  `,
  filler: css`
    label: MAIN_NAVIGATION_FILLER_STYLE;
    flex: 1;
  `,
  actionsContainer: css`
    label: MAIN_NAVIGATION_ACTIONS_STYLE;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  `,
};

// Consumed via a raw <button css={...}> (Emotion's css prop), not
// CommonActionButton's `style` prop - CommonActionButton only reads an inline
// style attribute, which can't express `:hover` at all, and Emotion's JSX
// pragma strips `css` before a component (as opposed to a DOM element) ever
// sees it as a prop. A plain DOM element is what lets this work simply.
export const MAIN_NAVIGATION_BUTTON_STYLES: MainNavigationButtonStyles = {
  debug: css`
    label: DEBUG_BUTTON_STYLE;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s ease;
    &:hover {
      background: rgba(255, 255, 255, 0.08)
    }
  `,
  primary: css`
    label: MAIN_NAVIGATION_PRIMARY_BUTTON_STYLE;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 18px;
    border: 1px solid ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.mainNavigationActionColor};
    border-radius: 0;
    background: ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.mainNavigationActionColor};
    color: ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.mainNavigationActionTextColor};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(11)};
    font-weight: ${KAI_SHIKI_TYPOGRAPHY.fontWeight.bold};
    line-height: 1;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      background: ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.mainNavigationActionHoverColor};
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
      background: ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.mainNavigationActionColor};
    }

    &:disabled:hover {
      background: ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.mainNavigationActionColor};
    }
  `,
  secondary: css`
    label: MAIN_NAVIGATION_SECONDARY_BUTTON_STYLE;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 18px;
    border: 1px solid ${withOpacity(KAI_SHIKI_SETTINGS_WIDGET_COLORS.white, 0.35)};
    border-radius: 0;
    background: transparent;
    color: ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.white};
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(11)};
    font-weight: ${KAI_SHIKI_TYPOGRAPHY.fontWeight.bold};
    line-height: 1;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      border-color: ${KAI_SHIKI_SETTINGS_WIDGET_COLORS.white};
      background: ${withOpacity(KAI_SHIKI_SETTINGS_WIDGET_COLORS.white, 0.06)};
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }

    &:disabled:hover {
      border-color: ${withOpacity(KAI_SHIKI_SETTINGS_WIDGET_COLORS.white, 0.35)};
      background: transparent;
    }
  `,
};
